import React from "react";
import { StyleSheet, View, TouchableOpacity, Platform } from "react-native";
import { ShareModel, ShareSummary, ICustomCardEntityConfig } from "common247";
import { Ionicons } from "@expo/vector-icons";
import { EntityIcon } from "@/components/ui/EntityIcon";
import { ThemedText } from "../ThemedText";
import { ThemedView } from "../ThemedView";
import { ThemedEntityLink } from "../ThemedEntityLink";
import { ThemedSegmentedControl } from "../ThemedSegmentedControl";
import { ThemedKebabMenu, type IKebabActionItem } from "../ThemedKebabMenu";
import { useThemeColor } from "../../hooks/useThemeColor";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedListViewMode } from "../ThemedList";
import { ThemedPlatformSymbol } from "../ThemedPlatformSymbol";
import { ThemedChip } from "../ThemedChip";
import { ThemedCompactCard } from "../cards/ThemedCompactCard";
import { ThemedFullCard } from "../cards/ThemedFullCard";
import { ThemedCustomCard } from "../cards/ThemedCustomCard";
import { router } from "expo-router";

/**
 * Props for the SharingCard component.
 */
export interface ISharingCardProps {
  share: ShareModel | ShareSummary;
  subtitle?: string;
  onUnshare?: (shareId: string) => void;
  onRoleChange?: (newRole: "viewer" | "editor") => void;
  canShare?: boolean;
  canUnshare?: boolean;
  mini?: boolean;
  compact?: boolean;
  viewMode?: ThemedListViewMode;
  /** Whether this card is currently hovered in the parent list */
  isHovered?: boolean;
  selectionMode?: boolean;
  isSelected?: boolean;
  onPress?: () => void;
  onLongPress?: () => void;
  onContextMenu?: (e?: any) => void;
  /** Optional custom card configuration */
  config?: ICustomCardEntityConfig;
}

/**
 * Archival card component displaying a share entry.
 *
 * Adheres strictly to the UX standard:
 * 1. Shows the item being shared (source entity) FIRST with an interactive ThemedEntityLink.
 * 2. Shows the recipient (who it is shared with) SECOND with an interactive ThemedEntityLink badge.
 * 3. Rendered as an archival card container (ThemedView), NOT a button.
 */
export function SharingCard({
  share,
  subtitle,
  onUnshare,
  onRoleChange,
  canShare = true,
  canUnshare = true,
  mini,
  compact = false,
  viewMode,
  isHovered,
  selectionMode = false,
  isSelected = false,
  onPress,
  onLongPress,
  onContextMenu,
  config,
}: ISharingCardProps) {
  const isSmall = viewMode === "small" || viewMode === "compact" || compact;

  const cardBgColor = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const hoverBorderColor = useThemeColor({}, "buttonHoverBorder");
  const tintColor = useThemeColor({}, "tint");
  const textMuted = useThemeColor({}, "textMuted");
  const badgeBg = useThemeColor({}, "buttonSecondary");

  const activeBorderColor = isSelected ? tintColor : isHovered ? hoverBorderColor : borderColor;

  const isFullModel = "sourceId" in share;
  const shareId = (share as ShareModel).id;
  const sourceType = isFullModel ? (share as ShareModel).sourceType || "memory" : "memory";
  const sourceId = isFullModel ? (share as ShareModel).sourceId : "";
  const sourceDisplayName = isFullModel ? ((share as ShareModel).sourceDisplayName || sourceId) : "";

  const targetType = isFullModel ? (share as ShareModel).targetType || "contact" : (share as ShareSummary).type || "contact";
  const targetId = isFullModel ? (share as ShareModel).targetId : (share as ShareSummary).id;
  const targetDisplayName = isFullModel
    ? ((share as ShareModel).targetDisplayName || targetId)
    : ((share as ShareSummary).displayName || targetId);
  const targetUserId = (share as any).targetUserId;
  const role = (share as ShareModel).role;
  const sharedAt = isFullModel ? (share as ShareModel).sharedAt : undefined;
  const sharedByDisplayName = isFullModel ? (share as ShareModel).sharedByDisplayName : undefined;

  const isRoleEditable = canShare && !!onRoleChange;

  const confirmRevoke = () => {
    CustomAlert.alert(
      "Revoke Access",
      `Are you sure you want to revoke access to "${sourceDisplayName || "this item"}" for ${targetDisplayName || "this recipient"}?`,
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Revoke",
          style: "destructive",
          onPress: () => onUnshare && shareId && onUnshare(shareId),
        },
      ]
    );
  };

  const kebabActions: IKebabActionItem[] = [
    ...(shareId
      ? [
          {
            id: "manage-share",
            label: "Manage Share",
            icon: "settings-outline",
            onPress: () => {
              if (onPress && !selectionMode) {
                onPress();
              } else {
                router.push(`/(tabs)/my-shares/${shareId}`);
              }
            },
          },
        ]
      : []),
    ...(sourceId
      ? [
          {
            id: "jump-source",
            label: `Open ${sourceType.charAt(0).toUpperCase() + sourceType.slice(1)}`,
            icon: "open-outline",
            onPress: () => {
              const route = `/(tabs)/my-${sourceType === "memory" ? "memories" : sourceType === "media" ? "media" : sourceType === "contact" ? "contacts" : "groups"}/${sourceId}`;
              router.push(route as any);
            },
          },
        ]
      : []),
    ...(targetId
      ? [
          {
            id: "jump-target",
            label: `View ${targetType.charAt(0).toUpperCase() + targetType.slice(1)}`,
            icon: "person-outline",
            onPress: () => {
              const route = `/(tabs)/my-${targetType === "contact" ? "contacts" : "groups"}/${targetId}`;
              router.push(route as any);
            },
          },
        ]
      : []),
    ...(canUnshare && onUnshare && shareId
      ? [
          {
            id: "revoke-share",
            label: "Revoke Access",
            icon: "trash-outline",
            destructive: true,
            onPress: confirmRevoke,
          },
        ]
      : []),
  ];

  if (viewMode === "chip") {
    return (
      <View style={{ margin: 2 }}>
        <ThemedChip
          entityType="share"
          id={shareId}
          label={sourceDisplayName || targetDisplayName || "Share"}
          onPress={onPress}
        />
      </View>
    );
  }

  if (viewMode === "compact") {
    return (
      <ThemedCompactCard
        entityType="share"
        id={shareId}
        sharedItem={{ entityType: (sourceType as any) || "memory", title: sourceDisplayName || "Shared Item" }}
        recipientName={targetDisplayName}
        selectionMode={selectionMode}
        isSelected={isSelected}
        onSelect={onPress}
        onPress={onPress}
        onLongPress={onLongPress}
        onContextMenu={onContextMenu}
        testID={`sharing-compact-card-${shareId || sourceId || "item"}`}
      />
    );
  }

  if (viewMode === "full") {
    const formattedDate = sharedAt
      ? `Shared ${new Date(sharedAt).toLocaleDateString()}`
      : undefined;

    const resolvedContact = targetType === "contact" && targetId
      ? [{ id: targetId, contactId: targetId, displayName: targetDisplayName || "Contact" }]
      : [];
    const resolvedGroup = targetType === "group" && targetId
      ? [{ id: targetId, groupId: targetId, name: targetDisplayName || "Group" }]
      : [];
    const resolvedMedia = sourceType === "media" && sourceId
      ? [{ id: sourceId, mediaId: sourceId, title: sourceDisplayName || "Media" }]
      : [];
    const resolvedMemories = sourceType === "memory" && sourceId
      ? [{ id: sourceId, memoryId: sourceId, title: sourceDisplayName || "Memory" }]
      : [];

    return (
      <ThemedFullCard
        entityType="share"
        id={shareId}
        title={sourceDisplayName || "Shared Item"}
        formattedDate={formattedDate}
        description={`Shared with ${targetDisplayName}${role ? ` (${role})` : ""}`}
        ownerDisplayName={sharedByDisplayName}
        userRole={role}
        kebabActions={kebabActions}
        contactsList={resolvedContact}
        groupsList={resolvedGroup}
        mediaList={resolvedMedia}
        memoriesList={resolvedMemories}
        onPress={onPress}
      />
    );
  }

  if (viewMode === "custom") {
    return (
      <ThemedCustomCard
        entityType="share"
        id={shareId}
        item={share}
        title={sourceDisplayName || "Shared Item"}
        recipientDisplayName={targetDisplayName}
        kebabActions={kebabActions}
        config={config}
        onPress={onPress}
      />
    );
  }

  if (mini) {
    return (
      <View style={[styles.miniCard, { backgroundColor: badgeBg }]}>
        <EntityIcon type={sourceType} size={13} color={tintColor} style={styles.miniIcon} />
        <ThemedText style={styles.miniName} numberOfLines={1}>
          {sourceDisplayName || targetDisplayName}
        </ThemedText>
        {targetUserId ? <Ionicons name="checkmark-circle" size={10} color="#1e8e3e" style={{ marginLeft: 2 }} /> : null}
        {role === "editor" && <View style={[styles.editorDot, { backgroundColor: tintColor }]} />}
      </View>
    );
  }

  return (
    <ThemedView
      style={[
        styles.card,
        {
          backgroundColor: cardBgColor,
          borderColor: activeBorderColor,
        },
      ]}
      testID={`sharing-card-${shareId || sourceId || "item"}`}
      {...(Platform.OS === "web" && onContextMenu
        ? {
            onContextMenu: (e: any) => {
              e.preventDefault();
              onContextMenu(e);
            },
          }
        : {})}
    >
      <View style={styles.cardHeaderRow}>
        {/* Selection Checkbox */}
        {selectionMode || isSelected ? (
          <TouchableOpacity
            onPress={onPress}
            style={[
              styles.detailsCheckbox,
              isSelected && { backgroundColor: tintColor, borderColor: tintColor },
            ]}
            accessibilityLabel={isSelected ? "Deselect share" : "Select share"}
          >
            {isSelected && <Ionicons name="checkmark" size={12} color="#FFFFFF" />}
          </TouchableOpacity>
        ) : null}

        {/* Source Entity Icon */}
        <View style={[styles.iconContainer, { backgroundColor: badgeBg }]}>
          <EntityIcon type={sourceType} size={18} color={tintColor} />
        </View>

        {/* Header Source Title & Meta */}
        <View style={styles.headerTitleColumn}>
          {/* Row 1: Item Being Shared (FIRST) */}
          <View style={styles.sourceRow}>
            {sourceId ? (
              <ThemedEntityLink
                type={sourceType}
                id={sourceId}
                displayName={sourceDisplayName || `${sourceType} item`}
                variant="title"
                numberOfLines={1}
                textStyle={styles.sourceTitle}
                testID={`sharing-card-source-${shareId || sourceId}`}
              />
            ) : (
              <ThemedText type="editorialTitle" style={styles.sourceTitle} numberOfLines={1}>
                {sourceDisplayName || "Shared Item"}
              </ThemedText>
            )}
          </View>

          {/* Row 2: Metadata Stamp */}
          <View style={styles.metadataRow}>
            <ThemedText style={[styles.metadataText, { color: textMuted }]} numberOfLines={1}>
              {`Shared ${sharedAt ? new Date(sharedAt).toLocaleDateString() : ""}${sharedByDisplayName ? ` • by ${sharedByDisplayName}` : ""}`}
            </ThemedText>
          </View>
        </View>

        {/* Top-Right Platform Symbol & Kebab Menu */}
        <View style={styles.topRightSymbolsRow}>
          <ThemedPlatformSymbol type="shared" size="small" />
          {kebabActions.length > 0 && (
            <ThemedKebabMenu
              actions={kebabActions}
              compact={compact}
              accessibilityLabel="Share options"
            />
          )}
        </View>
      </View>

      {/* Recipient Row: Who It Is Shared With (SECOND) */}
      <View style={styles.recipientRow}>
        <ThemedText style={[styles.recipientPrefix, { color: textMuted }]}>
          Shared with:
        </ThemedText>
        <ThemedChip
          entityType={targetType === "group" ? "group" : "contact"}
          id={targetId}
          label={targetDisplayName || targetId || "Recipient"}
          compact={true}
          testID={`sharing-card-target-${shareId || targetId}`}
        />
        {targetUserId ? (
          <Ionicons
            name="checkmark-circle"
            size={13}
            color="#1e8e3e"
            style={styles.verifiedIcon}
          />
        ) : null}
      </View>

      {/* Role / Access Control Action Row */}
      {role && (
        <View style={styles.actionsRow}>
          <ThemedSegmentedControl
            value={role}
            size={isSmall ? "small" : "medium"}
            readOnly={!isRoleEditable}
            onChange={(newRole) => {
              if (newRole === "revoke") {
                confirmRevoke();
              } else if (newRole === "viewer" || newRole === "editor") {
                onRoleChange?.(newRole);
              }
            }}
            options={[
              {
                icon: "eye-outline",
                label: isSmall ? undefined : "Viewer",
                accessibilityLabel: "Viewer",
                value: "viewer",
              },
              {
                icon: "pencil-outline",
                label: isSmall ? undefined : "Editor",
                accessibilityLabel: "Editor",
                value: "editor",
              },
              ...(canUnshare && onUnshare && shareId
                ? [
                    {
                      icon: "trash-outline" as const,
                      label: isSmall ? undefined : "Revoke",
                      accessibilityLabel: "Revoke Access",
                      value: "revoke",
                      destructive: true,
                    },
                  ]
                : []),
            ]}
          />
        </View>
      )}
    </ThemedView>
  );
}

const styles = StyleSheet.create({
  card: {
    width: "100%",
    alignSelf: "stretch",
    borderRadius: 4,
    padding: 12,
    marginBottom: 4,
    borderWidth: 1,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 1,
  },
  cardHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    gap: 10,
  },
  detailsCheckbox: {
    width: 20,
    height: 20,
    borderRadius: 3,
    borderWidth: 1.5,
    borderColor: "rgba(150, 150, 150, 0.4)",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 4,
  },
  iconContainer: {
    width: 38,
    height: 38,
    borderRadius: 4,
    justifyContent: "center",
    alignItems: "center",
    alignSelf: "flex-start",
    marginTop: 1,
  },
  headerTitleColumn: {
    flex: 1,
    minWidth: 0,
    justifyContent: "center",
    gap: 2,
  },
  topRightSymbolsRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 4,
    alignSelf: "flex-start",
  },
  sourceRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "nowrap",
  },
  sourceTitle: {
    fontSize: 13,
    fontWeight: "600",
  },
  recipientRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: "rgba(150, 150, 150, 0.2)",
  },
  recipientPrefix: {
    fontSize: 11,
    marginRight: 2,
  },
  recipientBadgeText: {
    fontSize: 11,
  },
  verifiedIcon: {
    marginLeft: 2,
  },
  metadataRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  metadataText: {
    fontSize: 10,
  },
  actionsRow: {
    marginTop: 8,
    width: "100%",
  },
  miniCard: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 0,
    paddingHorizontal: 4,
    paddingVertical: 2,
    marginRight: 4,
    marginBottom: 2,
  },
  miniIcon: {
    marginRight: 3,
  },
  miniName: {
    fontSize: 9.5,
    fontWeight: "600",
    opacity: 0.7,
  },
  editorDot: {
    width: 3,
    height: 3,
    borderRadius: 0,
    marginLeft: 3,
  },
});
